import React, {useState} from "react";
import {DropdownButton, DropdownItem, DropdownItemGroup, DropdownItemGroups, Icon, useNotifications} from "peggirkit";
import {PrinterIcon} from "@heroicons/react/20/solid";
import {FireIcon, TruckIcon} from "@heroicons/react/16/solid";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {BuildingStorefrontIcon} from "@heroicons/react/24/outline";
import {PlacedOrderOverview, PosOrderPrint} from "@/data/entities/order";
import {FormattedMessage, useIntl} from "react-intl";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useMutation} from "@tanstack/react-query";
import {api} from "@/data/api";
import CourierPrintModal from "@widgets/pos/orders/details/order/actions/courier/CourierPrintModal";

type Props = {
    order: PlacedOrderOverview,
};

const PrintActions = ({order}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const [showCourierModal, setShowCourierModal] = useState(false);
    const {mutate, isPending} = useMutation({
        mutationFn: (print: PosOrderPrint) =>
            api.orders.printOrder(dashboardLocation.id, order.cartPublicId, print),
        onSuccess: () => notifySuccess(intl.formatMessage({id: "printJobQueued"}), notify, PrinterIcon),
        onError: () => notifyDanger(intl.formatMessage({id: "printJobFailed"}), notify),
    });

    // The print agent skips cancelled orders, so printing them is not possible
    const isCancelled = order.cancelledOn !== null;
    const printAction = (print: PosOrderPrint) => ({
        as: "button" as const,
        actionProps: {
            onClick: () => mutate(print),
        },
    });

    return (
        <>
            <DropdownButton
                color={"primary"}
                variant={"outline"}
                iconBefore={<Icon icon={PrinterIcon}/>}
                disabled={isCancelled}
                loading={isPending}
                items={
                    <DropdownItemGroups>
                        <DropdownItemGroup>
                            <DropdownItem
                                icon={<Icon icon={FireIcon}/>}
                                disabled={isPending || isCancelled}
                                buttonAction={printAction({
                                    deliveryReceipt: false,
                                    kitchenReceipt: true,
                                    customerReceipt: false,
                                })}
                            >
                                <FormattedMessage id={"kitchenReceipt"}/>
                            </DropdownItem>
                            <DropdownItem
                                icon={<Icon icon={BuildingStorefrontIcon}/>}
                                disabled={isPending || isCancelled}
                                buttonAction={printAction({
                                    deliveryReceipt: false,
                                    kitchenReceipt: false,
                                    customerReceipt: true,
                                })}
                            >
                                <FormattedMessage id={"customerReceipt"}/>
                            </DropdownItem>
                        </DropdownItemGroup>
                        <DropdownItemGroup>
                            <DropdownItem
                                icon={<Icon icon={TruckIcon}/>}
                                disabled={!order.delivery || isPending || isCancelled}
                                buttonAction={{
                                    as: "button",
                                    actionProps: {
                                        onClick: () => setShowCourierModal(true),
                                    },
                                }}
                            >
                                <FormattedMessage id={"deliveryReceipt"}/>
                            </DropdownItem>
                        </DropdownItemGroup>
                    </DropdownItemGroups>
                }
            >
                <FormattedMessage id={"printReceipt"}/>
            </DropdownButton>

            <CourierPrintModal
                show={showCourierModal}
                setShow={setShowCourierModal}
                order={order}
            />
        </>
    );
};

export default PrintActions;